/* App state, load/save, valuation helpers
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 392-406.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

import { BY_SID, CATALOG, SID_ALIAS, gradesOf } from './catalog.js'
import { KEY, storageSet } from './storage.js'

/* ⚠ `pubZoom` IS A SLOT NAME OR null, DELIBERATELY NOT A BOOLEAN. The
 * enlarge opens over ONE side of ONE note — front and back are separate
 * archive objects, several megabytes each — so an open viewer has to name
 * which one it is showing. A boolean would have made "the enlarge is
 * open" a fact about the screen rather than about a photograph, and the
 * second frame would have had nowhere to say it was the subject.
 *
 * ⚠⚠ `vaultZoom` IS THE SAME SHAPE FOR THE OWNER'S OWN NOTE, AND IT IS A
 * SEPARATE FIELD RATHER THAN A REUSED ONE. `pubZoom` is read by
 * `views-community.js` and by the Community branch of `hydratePhotos`;
 * a single shared field would mean an open vault viewer put a Community
 * screen into a state it has code for, and vice versa. The two viewers
 * are deliberately not the same function (Jesse, 2026-09-03) and this is
 * the same boundary one level down. `screen` already separates them, but
 * separating them ONLY by `screen` is the kind of agreement that holds
 * today and stops holding the first time a screen is added. */
/* ⚠⚠⚠ `pending` IS ON `state` AND NEVER ON A HOLDING — step 4.1.
 * `save()` serialises `state.collection`, `snapshot()` projects a NAMED
 * SET of keys off each holding (~~six~~ — it is seven since db/012, and
 * the number is not what makes the argument work) and `holdingToRow()`
 * builds a row from named fields, so a map that is not ON a holding
 * cannot reach any of the three. See `src/pending.js`; `pending-check` proves it. */
let state={pending:{},screen:"collection",pubNote:null,pubZoom:null,vaultZoom:null,collection:[],sel:null,addGrade:null,search:"",filter:"All",vSearch:"",vSort:"added",vType:"All",vDenom:"All",vSeries:"All",vBack:"All",username:"",isPublic:false,cSearch:"",viewUser:null,editingName:false,pubProfiles:[],pSort:"added",pType:"All",pDenom:"All",pSeries:"All",pBack:"All",pSearch:"",fCtx:"vault",usernameSetAt:0};

/* storage */
/* ⚠ RETURNS ITS ERROR, AND NO LONGER TOASTS IT — the offline-reload item, his go 2026-10-18. `bootSession`
 * (its one caller) decides what a failed read means: the "didn't load" panel, or today's toast when signed out. */
async function load(){let err=null;try{const r=await window.storage.get(KEY,false);state.collection=r?JSON.parse(r.value):[];}catch(e){state.collection=[];console.error("vault read failed",e);err=e;}migrateIds();return err;}
function migrateIds(){let n=0;state.collection.forEach(h=>{if(typeof h.catId==="number"){const c=CATALOG[h.catId];if(c){h.catId=c.sid;n++;}}});if(n){save();console.log("migrated",n,"holdings to permanent IDs");}}
/* ⚠ THE SAVE SAYS HOW IT ENDED, AS A DOM EVENT — the offline-add item, his ruling 2026-10-22. `ck:saved` on
 * success, `ck:savefailed` with the error's `status` on failure (0 is remote.js's "never reached the server"
 * path). `main.js` listens and shows or hides the offline bar; nothing here retries, keeps a copy, or marks a
 * holding. Guarded so the gates that drive `save()` under bare node (no document) are unchanged. */
/* ⚠ THE EVENT IS BUILT FROM THE DOCUMENT'S OWN WINDOW (`defaultView`), not the global `CustomEvent`: in the
 * browser they are the same object; under jsdom (search-check) the global is Node's and the document refuses it. */
function tell(name,detail){try{if(typeof document==="undefined")return;const Ev=(document.defaultView&&document.defaultView.CustomEvent)||(typeof CustomEvent==="function"?CustomEvent:null);if(Ev)document.dispatchEvent(new Ev(name,{detail}));}catch(e){}}
async function save(){try{await storageSet(KEY,JSON.stringify(state.collection),false);tell("ck:saved",null);}catch(e){console.error(e);tell("ck:savefailed",{status:(e&&typeof e.status==="number")?e.status:null});toast((e&&e.userMessage)||"Could not save — this change is not stored yet");}}
const $=id=>document.getElementById(id);
const money=n=>n==null?"—":"$"+n.toLocaleString("en-US");
const cat=ref=>{if(typeof ref==="string"){if(BY_SID[ref])return BY_SID[ref];const a=SID_ALIAS[ref];if(a&&BY_SID[a])return BY_SID[a];return undefined;}return CATALOG.find(n=>n.id===ref);};

/**
 * Resolve ONE note on somebody else's public profile.
 *
 * ⚠⚠⚠ THIS EXISTS BECAUSE THE SAME LINE WAS ABOUT TO BE WRITTEN A FOURTH
 * TIME. `x.n || cat(x.c)` lives at `views-community.js:34` (the card
 * list) and `filters.js:26` (the filter sheet), and Step 9's public note
 * detail is the third reader. The second one was NOT in the plan and was
 * found by sweeping every consumer of `pubProfiles` — so the next reader
 * will be found the same way or not at all.
 *
 * ⚠ THE TWO HALVES ARE NOT INTERCHANGEABLE AND BOTH ARE REQUIRED:
 *   x.n     a QUICK-ADD note. `remote.js`'s `rowToPublicNote` carries the
 *           four fields db/009 projects, rebuilt into an entry. A
 *           stranger's `BY_SID` has never held another collector's
 *           `CUSTOM:` sid, so `cat()` cannot resolve one and never will.
 *   cat(c)  a CATALOG note, and the demo profiles, which have no `n`.
 *
 * Undefined for a note that resolves to neither — every caller filters on
 * that, and a note that reaches a renderer unresolved is the vanishing
 * -note bug (db/004) arriving on the public side.
 */
const pubCat=x=>(x&&x.n)||cat(x&&x.c);
function bookVal(h){const n=cat(h.catId);if(!n)return null;const gs=gradesOf(n);const i=gs.indexOf(h.grade);return i<0?null:n.g[i];}
function estVal(h){if(h&&h.appr!==undefined&&h.appr!==""&&h.appr!==null)return +h.appr;return bookVal(h);}

function totals(){let v=0,c=0,priced=0;state.collection.forEach(h=>{const e=estVal(h);if(e!=null){v+=e;priced++;}c+=(+h.cost||0);});return{v,c,g:v-c,priced};}

function toast(t){const el=$("toast");el.textContent=t;el.classList.add("show");clearTimeout(el._t);el._t=setTimeout(()=>el.classList.remove("show"),1800);}

export {
  $,
  bookVal,
  cat,
  estVal,
  load,
  money,
  pubCat,
  save,
  state,
  toast,
  totals,
}
